import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useSocket } from './SocketContext';
import { useAuth } from './AuthContext';

const CallContext = createContext(null);
const ICE_CONFIG = { iceServers: [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: ['turn:8.211.150.250:3478?transport=udp', 'turn:8.211.150.250:3478?transport=tcp'], username: 'globalheart', credential: '93476bc18cafca1d' }
] };

export function CallProvider({ children }) {
  const { socket } = useSocket();
  const { user } = useAuth();
  const [callState, setCallState] = useState(null);
  const [callPartner, setCallPartner] = useState(null);
  const [callType, setCallType] = useState('audio');
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [micEnabled, setMicEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [facingMode, setFacingMode] = useState('user');
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const callTypeRef = useRef('audio');
  const localMediaRef = useRef(null);
  const remoteMediaRef = useRef(null);
  const pendingOffer = useRef(null);
  const pendingFrom = useRef(null);
  const pendingCandidates = useRef([]);

  const stopCall = useCallback(() => {
    localStreamRef.current?.getTracks().forEach(track => track.stop());
    peerRef.current?.close();
    peerRef.current = null;
    localStreamRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setCallState(null);
    setCallPartner(null);
    setCallType('audio');
    setMicEnabled(true);
    setCameraEnabled(true);
    setFacingMode('user');
    pendingOffer.current = null;
    pendingFrom.current = null;
    pendingCandidates.current = [];
  }, []);

  const mediaConstraints = useCallback(type => type === 'video' ? {
    audio: true,
    video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24, max: 30 } }
  } : { audio: true, video: false }, []);

  const createPeer = useCallback(targetUserId => {
    const peer = new RTCPeerConnection(ICE_CONFIG);
    peerRef.current = peer;
    peer.ontrack = event => setRemoteStream(event.streams[0]);
    peer.onicecandidate = event => {
      if (event.candidate) socket?.emit('ice_candidate', { targetUserId, candidate: event.candidate });
    };
    peer.oniceconnectionstatechange = () => {
      if (peer.iceConnectionState === 'failed') {
        window.alert((callTypeRef.current === 'video' ? '视频' : '语音') + '连接失败，请检查网络后重试。');
        stopCall();
      }
    };
    return peer;
  }, [socket, stopCall]);

  const attachLocalStream = useCallback((stream, peer) => {
    localStreamRef.current = stream;
    setLocalStream(stream);
    stream.getTracks().forEach(track => peer.addTrack(track, stream));
  }, []);

  const flushCandidates = useCallback(async () => {
    if (!peerRef.current?.remoteDescription) return;
    const queued = pendingCandidates.current.splice(0);
    for (const candidate of queued) {
      try { await peerRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch (_) {}
    }
  }, []);

  const startCall = useCallback(async (targetUserId, displayName, type = 'audio') => {
    if (!socket?.connected || callState) return;
    callTypeRef.current = type;
    setCallType(type);
    setCallState('calling');
    setCallPartner({ user_id: targetUserId, display_name: displayName });
    try {
      const stream = await navigator.mediaDevices.getUserMedia(mediaConstraints(type));
      const peer = createPeer(targetUserId);
      attachLocalStream(stream, peer);
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      socket.emit('call_user', { targetUserId, offer, callType: type });
    } catch (error) {
      stopCall();
      const permission = error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError';
      window.alert(permission ? `请允许${type === 'video' ? '摄像头和' : ''}麦克风权限后再通话。` : `${type === 'video' ? '视频' : '语音'}通话启动失败，请检查设备权限和网络。`);
    }
  }, [socket, callState, mediaConstraints, createPeer, attachLocalStream, stopCall]);

  const acceptCall = useCallback(async () => {
    const offer = pendingOffer.current;
    const from = pendingFrom.current;
    const type = callTypeRef.current;
    if (!offer || !from) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia(mediaConstraints(type));
      const peer = createPeer(from);
      attachLocalStream(stream, peer);
      await peer.setRemoteDescription(new RTCSessionDescription(offer));
      await flushCandidates();
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      socket.emit('call_accepted', { targetUserId: from, answer });
      setCallState('connected');
    } catch (error) {
      stopCall();
      const permission = error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError';
      window.alert(permission ? `请允许${type === 'video' ? '摄像头和' : ''}麦克风权限后再接听。` : `${type === 'video' ? '视频' : '语音'}通话接听失败，请检查设备权限和网络。`);
    }
  }, [socket, mediaConstraints, createPeer, attachLocalStream, flushCandidates, stopCall]);

  const rejectCall = useCallback(() => {
    if (socket?.connected && pendingFrom.current) socket.emit('call_rejected', { targetUserId: pendingFrom.current });
    stopCall();
  }, [socket, stopCall]);

  const hangUp = useCallback(() => {
    if (socket?.connected && callPartner) socket.emit('end_call', { targetUserId: callPartner.user_id });
    stopCall();
  }, [socket, callPartner, stopCall]);

  const toggleMicrophone = useCallback(() => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMicEnabled(track.enabled);
  }, []);

  const toggleCamera = useCallback(() => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setCameraEnabled(track.enabled);
  }, []);

  const switchCamera = useCallback(async () => {
    if (callTypeRef.current !== 'video' || !peerRef.current) return;
    const oldTrack = localStreamRef.current?.getVideoTracks()[0];
    const sender = peerRef.current.getSenders().find(item => item.track?.kind === 'video');
    if (!oldTrack || !sender) return;
    const oldSettings = oldTrack.getSettings?.() || {};
    const nextFacing = facingMode === 'user' ? 'environment' : 'user';
    let replacementStream;
    try {
      const devices = (await navigator.mediaDevices.enumerateDevices()).filter(device => device.kind === 'videoinput');
      const currentIndex = devices.findIndex(device => device.deviceId === oldSettings.deviceId);
      const target = devices.length > 1 ? devices[(currentIndex >= 0 ? currentIndex + 1 : 1) % devices.length] : null;
      oldTrack.stop();
      replacementStream = await navigator.mediaDevices.getUserMedia({
        video: target?.deviceId ? { deviceId: { exact: target.deviceId }, facingMode: nextFacing, width: { ideal: 640 }, height: { ideal: 480 } } : { facingMode: { exact: nextFacing }, width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      });
      const newTrack = replacementStream.getVideoTracks()[0];
      if (!newTrack) throw new Error('No video track');
      await sender.replaceTrack(newTrack);
      localStreamRef.current.getVideoTracks().forEach(track => localStreamRef.current.removeTrack(track));
      localStreamRef.current.addTrack(newTrack);
      setLocalStream(new MediaStream(localStreamRef.current.getTracks()));
      setFacingMode(nextFacing);
      setCameraEnabled(true);
    } catch (error) {
      try {
        const restored = await navigator.mediaDevices.getUserMedia({ video: oldSettings.deviceId ? { deviceId: { exact: oldSettings.deviceId } } : { facingMode }, audio: false });
        const restoredTrack = restored.getVideoTracks()[0];
        await sender.replaceTrack(restoredTrack);
        localStreamRef.current.getVideoTracks().forEach(track => localStreamRef.current.removeTrack(track));
        localStreamRef.current.addTrack(restoredTrack);
        setLocalStream(new MediaStream(localStreamRef.current.getTracks()));
      } catch (_) {}
      window.alert('摄像头切换失败：' + (error?.name || '设备不支持') + '。请确认相机未被其他应用占用。');
    }
  }, [facingMode]);

  useEffect(() => {
    if (!socket || !user) return;
    const onIncomingCall = data => {
      const type = data.callType === 'video' ? 'video' : 'audio';
      callTypeRef.current = type;
      setCallType(type);
      setCallState('ringing');
      setCallPartner({ user_id: data.from, display_name: '' });
      pendingOffer.current = data.offer;
      pendingFrom.current = data.from;
      pendingCandidates.current = [];
      fetch('/api/profile/' + data.from).then(response => response.json()).then(profile => setCallPartner(previous => ({ ...previous, display_name: profile.display_name || profile.username || '' }))).catch(() => {});
    };
    const onCallAnswered = async data => {
      if (!peerRef.current) return;
      try { await peerRef.current.setRemoteDescription(new RTCSessionDescription(data.answer)); await flushCandidates(); setCallState('connected'); } catch (_) { stopCall(); }
    };
    const onCallRejected = () => { window.alert('对方已拒绝通话。'); stopCall(); };
    const onIceCandidate = async data => {
      if (!data.candidate) return;
      if (peerRef.current?.remoteDescription) {
        try { await peerRef.current.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch (_) {}
      } else pendingCandidates.current.push(data.candidate);
    };
    const onCallEnded = () => stopCall();
    socket.on('incoming_call', onIncomingCall);
    socket.on('call_answered', onCallAnswered);
    socket.on('call_rejected', onCallRejected);
    socket.on('ice_candidate', onIceCandidate);
    socket.on('call_ended', onCallEnded);
    return () => {
      socket.off('incoming_call', onIncomingCall);
      socket.off('call_answered', onCallAnswered);
      socket.off('call_rejected', onCallRejected);
      socket.off('ice_candidate', onIceCandidate);
      socket.off('call_ended', onCallEnded);
    };
  }, [socket, user, flushCandidates, stopCall]);

  useEffect(() => {
    if (localMediaRef.current && localStream) localMediaRef.current.srcObject = localStream;
  }, [localStream]);
  useEffect(() => {
    if (remoteMediaRef.current && remoteStream) {
      remoteMediaRef.current.srcObject = remoteStream;
      remoteMediaRef.current.play().catch(() => {});
    }
  }, [remoteStream]);

  const value = { callState, callPartner, callType, localStream, remoteStream, startCall, acceptCall, rejectCall, hangUp, toggleMicrophone, toggleCamera, switchCamera, micEnabled, cameraEnabled };
  const videoCall = callType === 'video';
  const controlStyle = { width:56, height:56, borderRadius:'50%', border:'none', color:'white', fontSize:'1.25rem', cursor:'pointer', background:'rgba(20,20,28,.78)' };

  return <CallContext.Provider value={value}>
    {children}
    {callState && <div style={{ position:'fixed', inset:0, zIndex:99999, background:'#090b12', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
      {videoCall && remoteStream ? <video ref={remoteMediaRef} autoPlay playsInline style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }} /> : null}
      {videoCall && localStream ? <video ref={localMediaRef} autoPlay muted playsInline style={{ position:'absolute', top:18, right:18, width:'28vw', maxWidth:180, minWidth:110, aspectRatio:'3/4', objectFit:'cover', borderRadius:16, border:'2px solid rgba(255,255,255,.7)', zIndex:2, transform:facingMode === 'user' ? 'scaleX(-1)' : 'none' }} /> : null}
      {!videoCall && <>
        <audio ref={localMediaRef} autoPlay muted playsInline style={{display:'none'}} />
        <audio ref={remoteMediaRef} autoPlay playsInline style={{display:'none'}} />
        <div style={{width:88,height:88,borderRadius:'50%',background:'linear-gradient(135deg,#4f7cff,#ff6b9d)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'2rem',color:'white',fontWeight:700}}>{(callPartner?.display_name || '?').charAt(0).toUpperCase()}</div>
      </>}
      <div style={{position:videoCall?'absolute':'static',top:videoCall?26:'auto',left:videoCall?24:'auto',zIndex:3,textAlign:videoCall?'left':'center',textShadow:'0 2px 8px #000'}}>
        <div style={{color:'white',fontSize:'1.15rem',fontWeight:600}}>{callPartner?.display_name || (videoCall ? '视频通话' : '语音通话')}</div>
        <div style={{color:'rgba(255,255,255,.75)',fontSize:'.9rem',marginTop:6}}>{callState === 'calling' ? `正在发起${videoCall?'视频':'语音'}通话...` : callState === 'ringing' ? `${videoCall?'视频':'语音'}来电...` : '通话中'}</div>
      </div>
      <div style={{position:videoCall?'absolute':'static',bottom:videoCall?34:'auto',zIndex:4,display:'flex',gap:16,marginTop:videoCall?0:28,alignItems:'center'}}>
        {callState === 'ringing' ? <>
          <button onClick={acceptCall} style={{...controlStyle,background:'#22c55e'}} title="接听">{videoCall?'📹':'📞'}</button>
          <button onClick={rejectCall} style={{...controlStyle,background:'#ef4444'}} title="拒绝">✕</button>
        </> : <>
          <button onClick={toggleMicrophone} style={controlStyle} title={micEnabled?'静音':'打开麦克风'}>{micEnabled?'🎙️':'🔇'}</button>
          {videoCall && <button onClick={toggleCamera} style={controlStyle} title={cameraEnabled?'关闭摄像头':'打开摄像头'}>{cameraEnabled?'📹':'🚫'}</button>}
          {videoCall && <button onClick={switchCamera} style={controlStyle} title="切换摄像头">🔄</button>}
          <button onClick={hangUp} style={{...controlStyle,background:'#ef4444'}} title="挂断">🔴</button>
        </>}
      </div>
    </div>}
  </CallContext.Provider>;
}

export function useCall() { return useContext(CallContext); }
